import React from "react";
import { AxiosError } from "axios";
import { Alert as JoyUiAlert } from "@mui/joy";
import { SxProps } from "@mui/joy/styles/types";
import { CheckCircle, AlertCircle, AlertTriangle } from "lucide-react";

interface IApiErrorResponse {
    StatusCode?: number;
    Success?: boolean;
    Error?:
        | {
              Name?: string;
              Message?: string;
              Stack?: string;
          }
        | string;
    message?: string;
}

interface IAlertProps {
    successMessage?: string | React.ReactElement | null | false;
    errorMessage?: string | boolean | Error | AxiosError | IApiErrorResponse | unknown | null | false;
    warningMessage?: string | React.ReactElement | null | false;
    sx?: SxProps;
    [key: string]: any;
}

/**
 * Parses API error responses and extracts user-friendly error messages
 */
export function parseError(
    error: string | boolean | Error | AxiosError | IApiErrorResponse | unknown | null | undefined
): string | null {
    if (!error) {
        return null;
    }

    // Handle boolean flags (legacy support)
    if (typeof error === "boolean") {
        return error ? "An error occurred. Please try again." : null;
    }

    // Handle string errors
    if (typeof error === "string") {
        return error;
    }

    // Handle Error objects
    if (error instanceof Error) {
        return error.message || "An error occurred. Please try again.";
    }

    // Handle AxiosError
    if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as AxiosError<IApiErrorResponse>;

        // Check if we have a response from the server
        if (axiosError.response) {
            const responseData = axiosError.response.data;

            // Check for API error structure: { StatusCode, Success, Error: { Name, Message } }
            if (responseData?.Error) {
                if (typeof responseData.Error === "string") {
                    // Validation errors come as strings: "Validation failed: error1, error2"
                    return responseData.Error;
                } else if (typeof responseData.Error === "object" && responseData.Error.Message) {
                    // Structured error: { Name, Message, Stack }
                    return responseData.Error.Message;
                }
            }

            // Fallback to response message
            if (responseData?.message) {
                return responseData.message;
            }

            // Fallback based on status code
            const statusCode = axiosError.response.status;
            if (statusCode === 401) {
                return "Unauthorized. Please log in again.";
            } else if (statusCode === 403) {
                return "You don't have permission to perform this action.";
            } else if (statusCode === 404) {
                return "The requested resource was not found.";
            } else if (statusCode === 500) {
                return "Server error. Please try again later.";
            } else if (statusCode >= 400 && statusCode < 500) {
                return "Invalid request. Please check your input and try again.";
            }
        }

        // Check if request was made but no response received (network error)
        if (axiosError.request && !axiosError.response) {
            return "No response from the server. Please check your internet connection or try again later.";
        }
    }

    // Handle API error response objects directly (not wrapped in AxiosError)
    if (error && typeof error === "object" && "Error" in error) {
        const apiError = error as IApiErrorResponse;
        if (typeof apiError.Error === "string") {
            return apiError.Error;
        } else if (apiError.Error && typeof apiError.Error === "object" && apiError.Error.Message) {
            return apiError.Error.Message;
        }
    }

    // Handle objects with message property
    if (error && typeof error === "object" && "message" in error) {
        const errorWithMessage = error as { message: string };
        return errorWithMessage.message;
    }

    // Default fallback
    return "An error occurred. Please try again.";
}

export default function Alert(props: IAlertProps) {
    const { successMessage, errorMessage, warningMessage, sx, ...otherProps } = props;

    // Default border styles that are automatically applied
    const defaultSx: SxProps = {
        border: "1px solid",
        borderColor: "divider",
    };

    // Merge default styles with custom sx prop
    // If sx is provided, merge it with defaultSx (custom sx will override defaults)
    const mergedSx: SxProps = sx
        ? [
              defaultSx,
              ...(Array.isArray(sx) ? sx : [sx]), // Handle both array and object sx
          ]
        : defaultSx;

    // Parse error message
    const parsedErrorMessage = parseError(errorMessage);

    if (parsedErrorMessage) {
        return (
            <JoyUiAlert
                variant="soft"
                startDecorator={<AlertCircle size={20} />}
                color="danger"
                sx={mergedSx}
                {...otherProps}
            >
                {parsedErrorMessage}
            </JoyUiAlert>
        );
    }

    // Handle warning message - can be string, React element, or falsy
    if (warningMessage) {
        // Check if it's a React element
        if (React.isValidElement(warningMessage)) {
            return (
                <JoyUiAlert
                    variant="soft"
                    startDecorator={<AlertTriangle size={24} />}
                    color="warning"
                    sx={mergedSx}
                    {...otherProps}
                >
                    {warningMessage}
                </JoyUiAlert>
            );
        }

        // Handle string warning message
        if (typeof warningMessage === "string") {
            return (
                <JoyUiAlert
                    variant="soft"
                    startDecorator={<AlertTriangle size={24} />}
                    color="warning"
                    sx={mergedSx}
                    {...otherProps}
                >
                    {warningMessage}
                </JoyUiAlert>
            );
        }
    }

    // Handle success message - can be string, React element, or falsy
    if (successMessage) {
        // Check if it's a React element
        if (React.isValidElement(successMessage)) {
            return (
                <JoyUiAlert
                    startDecorator={<CheckCircle size={20} />}
                    variant="soft"
                    color="success"
                    sx={mergedSx}
                    {...otherProps}
                >
                    {successMessage}
                </JoyUiAlert>
            );
        }

        // Handle string success message
        if (typeof successMessage === "string") {
            return (
                <JoyUiAlert
                    startDecorator={<CheckCircle size={20} />}
                    variant="soft"
                    color="success"
                    sx={mergedSx}
                    {...otherProps}
                >
                    {successMessage}
                </JoyUiAlert>
            );
        }
    }

    return null;
}
